import React from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Paper,
  Checkbox,
  ListItemText,
  Chip,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import arrow_down from "../../images/table/Arrow_down.svg";

export default function StopsSection({
  stops = [],
  selectedStops = [],
  routeData = {},
  onRouteDataChange,
  onStopToggle,
  onRemoveStop,
  onRemoveIntermediatePoint,
  errors,
  isOriginDestinationSet,
  routeType
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isXsScreen = useMediaQuery("(max-width:600px)");
  const isSmScreen = useMediaQuery("(max-width:900px)");

  // Filter stops for destination to exclude the selected origin when routeType is UNI_DIRCTIONAL or ONE_TIME_DELIVERY
  const filteredDestinationStops =
    (routeData.routeType === "UNI_DIRCTIONAL" || routeData.routeType === "ONE_TIME_DELIVERY") && routeData.origin
      ? stops.filter((stop) => stop.id !== routeData.origin.id)
      : stops;

  // Filter stops for origin to exclude the selected destination when routeType is UNI_DIRCTIONAL or ONE_TIME_DELIVERY
  const filteredOriginStops =
    (routeData.routeType === "UNI_DIRCTIONAL" || routeData.routeType === "ONE_TIME_DELIVERY") && routeData.destination
      ? stops.filter((stop) => stop.id !== routeData.destination.id)
      : stops;

  // Handle origin change - clear destination, stops, and points
  const handleOriginChange = (selectedStop) => {
    onRouteDataChange("origin", selectedStop);
    
    // Clear destination (except for BI_DIRECTIONAL which auto-sets it)
    if (routeData.routeType !== "BI_DIRECTIONAL") {
      onRouteDataChange("destination", null);
    } else {
      // For BI_DIRECTIONAL, set destination same as origin
      onRouteDataChange("destination", selectedStop);
    }
    
    // Clear intermediate stops
    onRouteDataChange("selectedStops", []);
    
    // Clear route points
    onRouteDataChange("routePoints", []);
  };

  // Check if origin is set for Round Route
  const isOriginSetForRoundRoute = routeType === "BI_DIRECTIONAL" && routeData.origin;

  return (
    <Paper
      elevation={0}
      sx={{
        width: isSmScreen ? "100%" : "100%",
        borderRadius: "10px",
        padding: "4px",
        gap: "16px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isXsScreen ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isXsScreen ? "flex-start" : "center",
          gap: isXsScreen ? 2 : 0,
          background: "#647490",
          padding: isXsScreen ? "16px" : "10px 16px",
          width: "100%",
          color: "#FFFFFF",
          borderRadius: 4,
          marginTop: "-4px",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontSize: "20px", fontWeight: 400, color: "#FFFFFF" }}
        >
          {t("Stops & Points on this Route")}
        </Typography>
      </Box>

      <Typography sx={{ fontSize: "16px", marginBottom: "0px" }}>
        {t("Origin")} <span style={{ color: "red", fontSize: "16px" }}>*</span>
      </Typography>
      <Select
        fullWidth
        IconComponent={() => (
          <img
            src={arrow_down}
            alt="arrow down"
            style={{ width: 20, height: 20, marginRight: 8 }}
          />
        )}
        value={routeData.origin?.id || ""}
        onChange={(e) => {
          const selectedStop = filteredOriginStops.find(
            (stop) => stop.id === e.target.value
          );
          handleOriginChange(selectedStop);
        }}
        sx={{
          height: 55,
          borderRadius: "16px",
          borderWidth: 2,
          padding: "10px 14px",
          background: "#FFFFFF",
          border: "2px solid #95A0B3",
          "& fieldset": {
            border: "none",
          },
          marginTop: "-16px",
        }}
        displayEmpty
      >
        <MenuItem value="">{t("Select start point from dropdown")}</MenuItem>
        {filteredOriginStops
          .sort((a, b) => parseFloat(a.longitude) - parseFloat(b.longitude))
          .map((stop) => (
            <MenuItem key={stop.id} value={stop.id}>
              {stop.name}
            </MenuItem>
          ))}
      </Select>
      {errors.origin && (
        <Typography color="error" variant="caption" sx={{ pl: 1 }}>
          {t("Origin is required")}
        </Typography>
      )}
      
      {/* Only show destination for non-BI_DIRECTIONAL routes */}
      {routeType !== "BI_DIRECTIONAL" && (
        <>
          <Typography>
            {t("Destination")} <span style={{ color: "red" }}>*</span>
          </Typography>
          <Select
            fullWidth
            value={routeData.destination?.id || ""}
            IconComponent={() => (
              <img
                src={arrow_down}
                alt="arrow down"
                style={{ width: 20, height: 20, marginRight: 8 }}
              />
            )}
            onChange={(e) => {
              const selectedStop = filteredDestinationStops.find(
                (stop) => stop.id === e.target.value
              );
              onRouteDataChange("destination", selectedStop);
            }}
            sx={{
              height: 55,
              borderRadius: "16px",
              borderWidth: 2,
              padding: "10px 14px",
              background: "#FFFFFF",
              border: "2px solid #95A0B3",
              "& fieldset": {
                border: "none",
              },
              marginTop: "-16px",
            }}
            displayEmpty
          >
            <MenuItem value="">{t("Select end point from dropdown")}</MenuItem>
            {filteredDestinationStops
              .sort((a, b) => parseFloat(a.longitude) - parseFloat(b.longitude))
              .map((stop) => (
                <MenuItem key={stop.id} value={stop.id}>
                  {stop.name}
                </MenuItem>
              ))}
          </Select>
          {errors.destination && (
            <Typography color="error" variant="caption" sx={{ pl: 1 }}>
              {t("Destination is required")}
            </Typography>
          )}
        </>
      )}

      {/* Intermediate Stops Dropdown */}
      <FormControl fullWidth>
        <Select
          labelId="stops-multiple-checkbox-label"
          id="stops-multiple-checkbox"
          IconComponent={() => (
            <img
              src={arrow_down}
              alt="arrow down"
              style={{ width: 20, height: 20, marginRight: 8 }}
            />
          )}
          displayEmpty
          renderValue={(selected) => {
            if (!selected || selected.length === 0) {
              return (
                <span style={{ color: "#183059", fontWeight: 500 }}>
                  {t("Select in-between stops to define the path")}
                  {routeType === "BI_DIRECTIONAL" && " *"}
                </span>
              );
            }
            return `${t("Selected Stops")}: ${selected.length}`;
          }}
          multiple
          value={selectedStops.map((stop) => stop.id)}
          onChange={(event) => {
            const selectedIds = event.target.value;
            const lastSelectedId = selectedIds[selectedIds.length - 1];
            const stopToToggle = stops.find(
              (stop) => stop.id === lastSelectedId
            );
            if (
              stopToToggle &&
              stopToToggle.id !== routeData?.origin?.id &&
              stopToToggle.id !== routeData?.destination?.id
            ) {
              onStopToggle(stopToToggle);
            }
          }}
          size="small"
          disabled={!isOriginSetForRoundRoute && !isOriginDestinationSet}
          MenuProps={{
            disableScrollLock: true,
            zIndex: 0,
            PaperProps: {
              style: {
                maxHeight: 224,
                width: 250,
              },
            },
          }}
          sx={{
            mt: 0,
            padding: "5px 8px",
            minHeight: 55,
            backgroundColor: "#FFF",
            borderRadius: "16px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#95A0B3",
              borderWidth: "2px",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#95A0B3",
            },
            "& .MuiSelect-select": {
              paddingTop: "12px",
              paddingBottom: "12px",
              color: "#183059",
            },
          }}
        >
          {stops
            .filter(
              (stop) =>
                stop.id !== routeData?.origin?.id &&
                stop.id !== routeData?.destination?.id
            )
            .sort((a, b) => parseFloat(b.longitude) - parseFloat(a.longitude))
            .map((stop) => (
              <MenuItem key={stop.id} onClick={() => onStopToggle(stop)}>
                <Checkbox
                  checked={selectedStops.some((s) => s.id === stop.id)}
                  disabled={!isOriginSetForRoundRoute && !isOriginDestinationSet}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                  }}
                />
                <ListItemText primary={stop.name} />
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      
      {/* Show error for Round Route if no intermediate stops */}
      {routeType === "BI_DIRECTIONAL" && selectedStops.length === 0 && (
        <Typography color="error" variant="caption" sx={{ pl: 1 }}>
          {t("At least one intermediate stop is required for Round Route")}
        </Typography>
      )}

      {/* Selected Stops Display Box */}
      <Box
        sx={{
          border: "1px solid #B7BFCC",
          flex: 1,
          minHeight: 0,
          borderRadius: "8px",
          borderWidth: "1px",
          padding: "12px",
          gap: "12px",
          overflowY: "auto",
          maxHeight:
            selectedStops.length === 0
              ? "calc(100% - 48px)"
              : "calc(100% - 48px - 224px)",
          transition: "max-height 0.3s ease",
        }}
      >
        {/* Selected stops and intermediate points section */}
        <Box sx={{ mt: 2 }}>
          {/* Display stops */}
          {selectedStops.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1} width="100%" mb={2}>
              {selectedStops.map((stop) => (
                <Chip
                  key={stop.id}
                  label={stop.name}
                  onDelete={() => onRemoveStop(stop)}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    bgcolor: "#E8EAEE",
                    color: "#008C23",
                    borderRadius: "4px",
                    height: 32,
                    "& .MuiChip-label": {
                      fontSize: "14px",
                    },
                    "& .MuiChip-deleteIcon": {
                      color: "#d32f2f",
                      width: 20,
                      height: 20,
                      border: "1px solid #d32f2f",
                      borderRadius: "50%",
                      p: 0.2,
                    },
                  }}
                />
              ))}
            </Stack>
          )}

          {/* Display intermediate points with black text */}
          {routeData.routePoints &&
            routeData.routePoints.filter((point) => !point.isStop).length >
              0 && (
              <Stack direction="row" flexWrap="wrap" gap={1} width="100%">
                {routeData.routePoints
                  .filter((point) => !point.isStop)
                  .map((point, index) => (
                    <Chip
                      key={`im-${index}-${point.id || Date.now()}`}
                      label={point.name}
                      onDelete={() =>
                        onRemoveIntermediatePoint(
                          point,
                          routeData.routePoints.indexOf(point)
                        )
                      }
                      deleteIcon={<CloseIcon />}
                      sx={{
                        bgcolor: "#E8EAEE",
                        color: "#183059",
                        borderRadius: "4px",
                        height: 32,
                        "& .MuiChip-label": {
                          fontSize: "14px",
                        },
                        "& .MuiChip-deleteIcon": {
                          color: "#d32f2f",
                          width: 20,
                          height: 20,
                          border: "1px solid #d32f2f",
                          borderRadius: "50%",
                          p: 0.2,
                        },
                      }}
                    />
                  ))}
              </Stack>
            )}

          {/* Show message when no stops or intermediate points */}
          {selectedStops.length === 0 &&
            (!routeData.routePoints ||
              routeData.routePoints.filter((point) => !point.isStop).length ===
                0) && (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  color: "text.secondary",
                  textAlign: "center",
                }}
              >
                <Typography>
                  {t(
                    "Select stops between origin and destination from the dropdown or by clicking on the map."
                  )}
                </Typography>
              </Paper>
            )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          borderRadius: "8px",
          padding: "12px",
          justifyContent: "space-between",
          border: "1px solid #B7BFCC",
          flexDirection: isXsScreen ? "column" : "row",
          alignItems: isXsScreen ? "stretch" : "center",
          gap: isXsScreen ? 1 : 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#183059",
            fontSize: "14px",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
        >
          {t("No stops to select?")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            navigate("/stop/add")
          }
          sx={{
            width: "auto",
            textTransform: "none",
            height: "35px",
            whiteSpace: "nowrap",
            borderRadius: 2,
            fontSize: "16px",
            fontWeight: 600,
            padding: "8px 18px",
            color: "#1F242E",
            backgroundColor: "#F9E719",
            border: "1px solid #414958",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "#F5E016",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              transform: "translateY(-1px)",
              border: "1px solid #2C3441",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
            },
            "&:focus": {
              outline: "2px solid #F9E719",
              outlineOffset: "2px",
            },
          }}
        >
          + {t("Create Stops")}
        </Button>
      </Box>
    </Paper>
  );
}